import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { Button } from './ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  ClipboardList, 
  LayoutDashboard, 
  Calendar, 
  Users, 
  Wrench, 
  UserCircle,
  Moon,
  Sun,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronDown,
  FileText,
  HelpCircle,
  Clock,
  Activity,
  Phone,
  AlertCircle,
  Package,
  Boxes,
  Sheet,
  StickyNote,
  Receipt,
  Wallet,
  BarChart3,
  CalendarDays
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const navItems = [
  { path: '/clients', label: 'Clientes', icon: Users },
  { path: '/agenda', label: 'Agenda de Trabajos', icon: CalendarDays },
  { path: '/asistencia', label: 'Asistencia', icon: UserCircle },
  { path: '/inventory', label: 'Inv. Línea B', icon: Package },
  { path: '/stock', label: 'Inv. Local', icon: Boxes },
  { path: '/pendientes', label: 'Pendientes', icon: StickyNote },
  { path: '/cotizaciones', label: 'Presupuestos', icon: Receipt },
  { path: '/caja', label: 'Caja REPACS', icon: Wallet },
  { path: '/control-financiero', label: 'Control Financiero', icon: BarChart3, adminOnly: true },
  { path: '/technicians', label: 'Trabajadores', icon: Wrench },
  { path: '/users', label: 'Usuarios', icon: UserCircle, adminOnly: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState({ today: [], tomorrow: [] });
  const [activities, setActivities] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);

  const fetchNotifications = useCallback(async () => {
    try {
      const [statsRes, activitiesRes, remindersRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/notifications/activities?limit=30`),
        axios.get(`${API}/notifications/upcoming`)
      ]);
      setNotifications(statsRes.data.notifications || { today: [], tomorrow: [] });
      setActivities(activitiesRes.data || []);
      setUpcomingReminders(remindersRes.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Actualizar notificaciones cada 30 segundos
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const totalNotifications = (notifications.today?.length || 0) + (notifications.tomorrow?.length || 0) + upcomingReminders.length;
  const urgentReminders = upcomingReminders.filter(r => r.urgent).length;

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b z-50 flex items-center justify-between px-4">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
          <Menu className="h-6 w-6" />
        </Button>
        <div className="flex items-center gap-2">
          <ClipboardList className="h-6 w-6 text-primary" />
          <span className="font-bold">REPACS SISTEMA</span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationDropdown 
            notifications={notifications} 
            activities={activities}
            upcomingReminders={upcomingReminders}
            onRefresh={fetchNotifications}
          />
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-card border-r z-50 transition-transform duration-300
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <ClipboardList className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg">REPACS SISTEMA</span>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            <nav className="space-y-1">
              {navItems.map((item) => {
                if (item.adminOnly && user?.role !== 'admin') return null;
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                      ${isActive 
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }
                    `}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User Section */}
          <div className="p-4 border-t">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-2">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <UserCircle className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm">{user?.full_name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={toggleTheme}>
                  {theme === 'dark' ? (
                    <>
                      <Sun className="mr-2 h-4 w-4" />
                      Tema Claro
                    </>
                  ) : (
                    <>
                      <Moon className="mr-2 h-4 w-4" />
                      Tema Oscuro
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Desktop Header */}
        <header className="hidden lg:flex h-16 items-center justify-between px-6 border-b bg-card/50 backdrop-blur sticky top-0 z-40">
          <div>
            <h1 className="text-xl font-semibold">
              {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <NotificationDropdown 
              notifications={notifications} 
              activities={activities}
              upcomingReminders={upcomingReminders}
              onRefresh={fetchNotifications}
            />
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 lg:p-6 pt-20 lg:pt-6">
          {children}
        </div>
      </main>
    </div>
  );
}

function NotificationDropdown({ notifications, activities, upcomingReminders, onRefresh }) {
  const navigate = useNavigate();
  const totalServices = (notifications.today?.length || 0) + (notifications.tomorrow?.length || 0);
  const urgentCount = upcomingReminders?.filter(r => r.urgent).length || 0;
  const total = totalServices + (upcomingReminders?.length || 0) + Math.min(activities?.length || 0, 10);

  const formatTimeAgo = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true, locale: es });
    } catch {
      return '';
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
          <Bell className={`h-5 w-5 ${urgentCount > 0 ? 'text-orange-500 animate-pulse' : ''}`} />
          {total > 0 && (
            <Badge className={`absolute -top-1 -right-1 h-5 min-w-5 p-0 flex items-center justify-center text-xs ${urgentCount > 0 ? 'bg-orange-500' : ''}`}>
              {total > 99 ? '99+' : total}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96">
        <div className="p-3 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Notificaciones</h3>
            <p className="text-xs text-muted-foreground">Centro de actividad</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onRefresh} className="h-8 px-2">
            <Activity className="h-4 w-4" />
          </Button>
        </div>
        
        <Tabs defaultValue="reminders" className="w-full">
          <TabsList className="w-full grid grid-cols-3 h-9">
            <TabsTrigger value="reminders" className="text-xs relative">
              Próximos
              {upcomingReminders?.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 px-1 text-xs">{upcomingReminders.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="services" className="text-xs">
              Servicios
              {totalServices > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 px-1 text-xs">{totalServices}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="activity" className="text-xs">
              Actividad
            </TabsTrigger>
          </TabsList>
          
          {/* Recordatorios de próximos servicios */}
          <TabsContent value="reminders" className="m-0">
            <ScrollArea className="max-h-80">
              {upcomingReminders?.length > 0 ? (
                <div className="p-2 space-y-1">
                  {upcomingReminders.map((reminder) => (
                    <div 
                      key={reminder.id}
                      onClick={() => navigate('/services')}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        reminder.urgent 
                          ? 'bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800' 
                          : 'hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {reminder.urgent && (
                              <AlertCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                            )}
                            <span className="font-medium text-sm truncate">{reminder.client_name}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{reminder.service_type}</p>
                          {reminder.phones?.[0] && (
                            <div className="flex items-center gap-1 mt-1">
                              <Phone className="h-3 w-3 text-muted-foreground" />
                              <span className="text-xs text-primary font-medium">{reminder.phones[0]}</span>
                            </div>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`font-bold ${reminder.urgent ? 'text-orange-600' : 'text-primary'}`}>
                            {reminder.service_time}
                          </p>
                          <p className={`text-xs ${reminder.urgent ? 'text-orange-600 font-medium' : 'text-muted-foreground'}`}>
                            {reminder.minutes_until <= 60 
                              ? `en ${reminder.minutes_until} min` 
                              : `en ${Math.floor(reminder.minutes_until / 60)}h ${reminder.minutes_until % 60}m`
                            }
                          </p>
                        </div>
                      </div>
                      {reminder.urgent && (
                        <p className="text-xs text-orange-600 mt-2 font-medium flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          ¡Recuerda marcarle al cliente!
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  No hay servicios próximos en las siguientes 2 horas
                </div>
              )}
            </ScrollArea>
          </TabsContent>
          
          {/* Servicios de hoy y mañana */}
          <TabsContent value="services" className="m-0">
            <ScrollArea className="max-h-80">
              {notifications.today?.length > 0 && (
                <div className="p-2">
                  <p className="text-xs font-medium text-muted-foreground px-2 mb-1 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> HOY
                  </p>
                  {notifications.today.map((service) => (
                    <div 
                      key={service.id}
                      onClick={() => navigate('/services')}
                      className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer"
                    >
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm block truncate">{service.client_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {service.service_time} - {service.service_type}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {notifications.tomorrow?.length > 0 && (
                <div className="p-2 border-t">
                  <p className="text-xs font-medium text-muted-foreground px-2 mb-1">MAÑANA</p>
                  {notifications.tomorrow.map((service) => (
                    <div 
                      key={service.id}
                      onClick={() => navigate('/services')}
                      className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer"
                    >
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm block truncate">{service.client_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {service.service_time} - {service.service_type}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {totalServices === 0 && (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  No hay servicios próximos
                </div>
              )}
            </ScrollArea>
          </TabsContent>
          
          {/* Actividad reciente */}
          <TabsContent value="activity" className="m-0">
            <ScrollArea className="max-h-80">
              {activities?.length > 0 ? (
                <div className="p-2 space-y-1">
                  {activities.slice(0, 20).map((activity, idx) => (
                    <div 
                      key={activity.id || idx}
                      className="p-2 rounded hover:bg-muted text-sm"
                    >
                      <div className="flex items-start gap-2">
                        <Activity className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm">
                            <span className="font-medium">{activity.user}</span>
                            {' '}{activity.action}{' '}
                            <span className="text-primary">{activity.entity_type}</span>
                            {activity.details && <span className="text-muted-foreground">{activity.details}</span>}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {formatTimeAgo(activity.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  No hay actividad reciente
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
